import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { pdfjs } from 'react-pdf';
import usePortal from 'react-useportal';
import type { Swiper as TSwiper } from 'swiper';
import { FreeMode, Navigation, Thumbs } from 'swiper/modules';
import { SwiperClass, SwiperSlide } from 'swiper/react';
import { useStore } from '../../hooks';
import { handleDownloadFile } from '../../utils/files';
import { AddExtraProps } from '../../utils/jsx';
import { RendererPropsData } from './types';
import { ImagePreviewer } from './components';
import {
  ClosePreviewWindowButton,
  DeleteButton,
  DocumentHeader,
  DocumentHeaderContent,
  DocumentName,
  DocumentNameInput,
  DocumentRendererContainer,
  DownloadButton,
  Stub,
  // GaleryContainer,
  // StyledDocViewer,
  // StyledDocViewerSlide,
  StyledSwiper,
  StyledSwiperCarousel,
  ThumbnailSwiperSlide,
  Wrapper,
} from './media-previewer.styled';
import { InlineSpinner } from '../loaders';
import { VideoPlayer } from '../video-player';

import {
  DocxRenderer,
  PdfRenderer,
  RtfRenderer,
  XlsxRenderer,
} from './renderers';

// /** Необходимый воркер */
// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//   'pdfjs-dist/build/pdf.worker.min.js',
//   import.meta.url
// ).toString();

// При попытке задать workerSrc иначе может либо упасть билд фронта, либо будет ошибка при обращении к воркеру.
// TODO Попытаться решить проблему без использования cdnjs
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const getRenderer = (fileObj: RendererPropsData) => {
  switch (fileObj?.file?.type?.toLowerCase()) {
    case 'application/pdf':
      return <PdfRenderer fileObj={fileObj} />;
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/octet-stream':
      return <DocxRenderer fileObj={fileObj} />;
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.ms-excel':
    case 'application/vnd.ms-excel.sheet.macroenabled.12':
    case 'application/vnd.ms-excel.addin.macroenabled.12':
    case 'application/vnd.ms-excel.sheet.binary.macroenabled.12':
    case 'application/vnd.ms-excel.template.macroenabled.12':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.template':
    case 'application/vnd.oasis.opendocument.spreadsheet':
    case 'text/csv':
      return <XlsxRenderer fileObj={fileObj} />;
    case 'application/rtf':
      return <RtfRenderer fileObj={fileObj} />;
    case 'video/mp4':
      return <VideoPlayer fileObj={fileObj} />;
    case 'image/jpeg':
    case 'image/png':
    case 'image/gif':
    case 'image/bmp':
    case 'image/webp':
      return <ImagePreviewer fileObj={fileObj} />;
    default:
      return (
        <Stub fileObj={fileObj} className="stab">
          Файл недоступен для предпросмотра
        </Stub>
      );
  }
};

interface IMediaPreviewerProps {
  fileRenameCallback: Function;
  fileDeleteCallback: Function;
  pendingCondition?: boolean;
}

export const MediaPreviewer = observer(
  ({
    fileRenameCallback,
    fileDeleteCallback,
    pendingCondition,
  }: IMediaPreviewerProps) => {
    const { Portal } = usePortal();
    const { previewer } = useStore();
    const documentNameRef = useRef<HTMLInputElement>(null);

    const [thumbsSwiper, setThumbsSwiper] = useState<TSwiper | null>(null);
    const [showEditDocumentNameInput, setShowEditDocumentNameInput] =
      useState<boolean>(false);
    const [lastValidDocumentName, setLastValidDocumentName] = useState<
      string | null
    >(null);
    const [currentDocumentName, setCurrentDocumentName] = useState<string>('');
    const [currentDocumentFormat, setCurrentDocumentFormat] =
      useState<string>('');
    const [splittedByRenderersJSX, setSplittedByRenderersJSX] = useState<any[]>(
      []
    );
    const [currentFileObj, setCurrentFileObj] = useState<{
      file: File;
      otherInformation: { [propName: string]: any };
    } | null>(null);
    const [transition, setTransition] = useState<boolean>(false);
    const clearFilesTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
      // В dev (React.StrictMode) React намеренно вызывает этот эффект как
      // mount -> cleanup -> mount синхронно, чтобы отловить отсутствующий
      // cleanup. Если cleanup чистит previewer.data синхронно, "холостой"
      // повторный mount читает уже опустошённые данные и тут же закрывает
      // окно сам (previewer.data.files.length === 0 -> setShowPreviewerWindow(false)
      // в эффекте ниже) — превью открывается и сразу гаснет.
      // Поэтому реальную очистку откладываем на макротаск и отменяем её,
      // если следом (в этом же тике) эффект успел смонтироваться снова.
      clearTimeout(clearFilesTimeoutRef.current);
      updateJSX();

      return () => {
        clearFilesTimeoutRef.current = setTimeout(() => {
          previewer.clearFiles();
        }, 0);
      };
    }, []);

    useEffect(() => {
      previewer.setShowPreviewerWindow(!!previewer.data.files.length);
    }, [previewer.data.files.length]);

    useEffect(() => {
      if (currentFileObj?.file) {
        const splittedDocumentFileName = currentFileObj.file.name.split('.');
        setCurrentDocumentName(splittedDocumentFileName[0]);
        setCurrentDocumentFormat(splittedDocumentFileName[1]);
      }
    }, [currentFileObj?.file]);

    useEffect(() => {
      if (showEditDocumentNameInput && documentNameRef.current) {
        documentNameRef.current.focus();
      }
    }, [showEditDocumentNameInput]);

    // Для чего? Вызывает лишние рендеры, сильно замедляет приложение
    // Видимо без этого теряется oldNamedFileIndex при повторном переименовании + instantUpdate
    // useEffect(() => {
    //   updateJSX();
    // }, [
    //   previewer.data.files[previewer.activeSlideIndex]?.otherInformation
    //     ?.fileId,
    // ]);

    // useEffect(() => {
    //   if (splittedByRenderersJSX.length) {
    //     setCurrentFileObj(
    //       (
    //         splittedByRenderersJSX[previewer.activeSlideIndex] ||
    //         splittedByRenderersJSX[previewer.activeSlideIndex - 1]
    //       ).props.fileObj
    //     );
    //   }
    // }, [splittedByRenderersJSX, previewer.activeSlideIndex]);

    useEffect(() => {
      if (splittedByRenderersJSX.length) {
        const activeSlideComponent =
          splittedByRenderersJSX[previewer.activeSlideIndex] ||
          splittedByRenderersJSX[previewer.activeSlideIndex - 1];

        if (activeSlideComponent) {
          setCurrentFileObj(activeSlideComponent.props.fileObj);
        }
      }
    }, [splittedByRenderersJSX, previewer.activeSlideIndex]);

    const updateJSX = (currentFileObj?: RendererPropsData, file?: File) => {
      if (!previewer.data.files.length) {
        return;
      }

      const toJSXData: any[] = [...previewer.data.files];

      if (currentFileObj) {
        file
          ? toJSXData.splice(previewer.activeSlideIndex, 1, {
              ...currentFileObj,
              file,
            })
          : toJSXData.splice(previewer.activeSlideIndex, 1);

        previewer.updateLocalFiles(toJSXData);
      }

      setSplittedByRenderersJSX(
        toJSXData
          .map((file: RendererPropsData) => getRenderer(file))
          /* getRenderer() вернёт null при отсутствии необходимого renderer */
          .filter((rendererJSX) => rendererJSX)
      );
    };

    const updateDocumentNameAndUnfocus = () => {
      if (lastValidDocumentName) {
        // при попытке сохранить файл с невалидным наименованием, будет возвращен последний валидный ввод
        setCurrentDocumentName(lastValidDocumentName);
        handleSetDocumentName(lastValidDocumentName, currentDocumentFormat);
      } else {
        handleSetDocumentName(currentDocumentName, currentDocumentFormat);
      }

      setShowEditDocumentNameInput(false);
      setLastValidDocumentName(null);
    };

    const handleSetDocumentName = (fileName: string, fileFormat: string) => {
      if (currentFileObj) {
        const renamedFile = new File(
          [currentFileObj.file],
          `${fileName}.${fileFormat}`,
          {
            type: currentFileObj.file.type,
          }
        );

        fileRenameCallback(currentFileObj.otherInformation, renamedFile);
        updateJSX(currentFileObj, renamedFile);
      }
    };

    const handleChangeDocumentName = (e: any) => {
      const newValue = e.target.value
        .replace(/[^а-яА-Яa-zA-Z0-9- _]/gi, '')
        .replaceAll('__', '_');

      if (
        (newValue.length <= 30 && !!newValue.replaceAll(' ', '').length) ||
        newValue < currentDocumentName
      ) {
        if (!newValue.length) {
          if (!lastValidDocumentName) {
            setLastValidDocumentName(currentDocumentName);
          }
        } else {
          setLastValidDocumentName(newValue);
        }

        setCurrentDocumentName(newValue);
      }
    };

    const handleDeleteFile = () => {
      if (currentFileObj) {
        fileDeleteCallback(currentFileObj);
        updateJSX(currentFileObj);
      }
    };

    return (
      <Portal>
        <Wrapper className="media-previewer-wrapper">
          <DocumentHeader>
            <DocumentHeaderContent>
              {pendingCondition && <InlineSpinner />}
              Файл {previewer.activeSlideIndex + 1} из{' '}
              {splittedByRenderersJSX.length}
              {showEditDocumentNameInput ? (
                <DocumentNameInput
                  disabled={pendingCondition}
                  ref={documentNameRef}
                  value={currentDocumentName}
                  onChange={handleChangeDocumentName}
                  onBlur={updateDocumentNameAndUnfocus}
                  onKeyPress={(e) =>
                    e.key === 'Enter' && updateDocumentNameAndUnfocus()
                  }
                />
              ) : (
                <DocumentName>
                  {currentDocumentName}.{currentDocumentFormat}
                </DocumentName>
              )}
              {/*//TODO: в ближайшем релизе не планируется*/}
              {/*Изменение имени файла не реализовано в новом API
              <EditButton
                onClick={() => setShowEditDocumentNameInput(true)}
                disabled={pendingCondition}
              /> */}
              <DeleteButton
                onClick={handleDeleteFile}
                className="close-preview"
                disabled={pendingCondition}
              >
                <i className="far fa-trash-alt" />
              </DeleteButton>
              <DownloadButton
                onClick={() =>
                  handleDownloadFile(
                    splittedByRenderersJSX[previewer.activeSlideIndex].props
                      .fileObj.file,
                    splittedByRenderersJSX[previewer.activeSlideIndex].props
                      .fileObj.file.name
                  )
                }
              />
              <ClosePreviewWindowButton
                onClick={() => previewer.clearFiles()}
              />
            </DocumentHeaderContent>
          </DocumentHeader>

          <StyledSwiper
            $transition={transition}
            onRealIndexChange={(swiper: SwiperClass) =>
              previewer.setActiveSlideIndex(swiper.activeIndex)
            }
            onTransitionStart={() => setTransition(true)}
            onTransitionEnd={() => setTransition(false)}
            allowTouchMove={false}
            spaceBetween={10}
            initialSlide={previewer.activeSlideIndex}
            navigation
            // Панель миниатюр рисуется только при >1 файле; когда она
            // размонтируется, Swiper уничтожается, а в state остаётся мёртвый
            // экземпляр — thumbs.init на нём падает (originalParams undefined).
            thumbs={{
              swiper:
                thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
            }}
            modules={[FreeMode, Navigation, Thumbs]}
            className="mySwiper2"
          >
            {splittedByRenderersJSX.map(
              (docRendererComponent: JSX.Element, index) => (
                <SwiperSlide key={index}>
                  <DocumentRendererContainer
                    slidesCount={splittedByRenderersJSX.length}
                  >
                    {AddExtraProps(docRendererComponent, {
                      isActive: previewer.activeSlideIndex === index,
                    })}
                  </DocumentRendererContainer>
                </SwiperSlide>
              )
            )}
          </StyledSwiper>

          {splittedByRenderersJSX.length > 1 && (
            <StyledSwiperCarousel
              slides={splittedByRenderersJSX.length}
              onSwiper={setThumbsSwiper}
              onDestroy={() => setThumbsSwiper(null)}
              spaceBetween={10}
              slidesPerView={4}
              freeMode
              watchSlidesProgress
              modules={[FreeMode, Navigation, Thumbs]}
              direction="vertical"
              className="mySwiper"
            >
              {splittedByRenderersJSX.map(
                (docRendererComponent: JSX.Element, index) => (
                  <ThumbnailSwiperSlide
                    key={index}
                    $active={previewer.activeSlideIndex === index}
                    className="swiper__slide-thumbnail"
                  >
                    {AddExtraProps(docRendererComponent, { thumbnail: true })}
                  </ThumbnailSwiperSlide>
                )
              )}
            </StyledSwiperCarousel>
          )}
        </Wrapper>
      </Portal>
    );
  }
);
